import {
  KdsButton,
  KdsCardFile,
  KdsText,
} from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { useState } from "react";

import { OrderItemReadDtoPreMediaStatusEnum } from "@/config/apiClient";
import { useIsOMS } from "@/hooks/useIsOMS";
import { ModalPreMediaBriefValidation } from "@/modals/ModalPreMediaBriefValidation";
import { ModalPreMediaPropositionFileForm } from "@/modals/ModalPreMediaPropositionFileForm";
import { ModalPreMediaRequestDetailsForm } from "@/modals/ModalPreMediaRequestDetailsForm";

import { type OrderItemDetailsPreMediaSectionProps } from "./OrderItemPreMediaSection.types";

export const OrderItemCSMPreMediaSection = ({
  orderItem,
}: OrderItemDetailsPreMediaSectionProps) => {
  const [validatePreMediaModalOpen, setValidatePreMediaModalOpen] =
    useState<boolean>(false);
  const [
    addPreMediaPropositionFileModalOpen,
    setAddPreMediaPropositionFileModalOpen,
  ] = useState<boolean>(false);
  const [preMediaRequestDetailsModalOpen, setPreMediaRequestDetailsModalOpen] =
    useState<boolean>(false);

  const { t } = useTranslate();
  const isOMS = useIsOMS();

  if (!orderItem.sandboxItem.withPreMedia) {
    return null;
  }

  // TODO: remove this when the backend is fixed
  // we dont need to check  the preMediaFiles length because the backend is not sending the correct preMediaStatus

  const preMediaToAccept: boolean =
    orderItem.preMediaStatus ===
      OrderItemReadDtoPreMediaStatusEnum.PreMediaSubmitted ||
    (orderItem.preMediaStatus === OrderItemReadDtoPreMediaStatusEnum.None &&
      !!orderItem.sandboxItem?.preMediaFiles?.length);

  const preMediaPropositionFileToAdd: boolean =
    orderItem.preMediaStatus ===
      OrderItemReadDtoPreMediaStatusEnum.PropositionRefused ||
    orderItem.preMediaStatus ===
      OrderItemReadDtoPreMediaStatusEnum.PreMediaAccepted;

  return (
    <div className="mt-sp-xs flex flex-col !gap-sp-md rounded-2xl bg-white !p-0 p-6">
      {/* File */}
      {(orderItem.sandboxItem.preMediaFiles || []).map(
        (preMediaRequestFile) => {
          return (
            <KdsCardFile
              key={preMediaRequestFile.url ?? preMediaRequestFile.blobId}
              fileName={preMediaRequestFile.fileName}
              fileUrl={preMediaRequestFile.url}
              fileSize={preMediaRequestFile.size}
              isDeletable
              isViewable
            />
          );
        }
      )}

      {/* Comment */}
      <div className="flex flex-col gap-sp-xs">
        <KdsText size="subtitle-sm">
          {t("order.myOrdersItemDetails.preMedia.comment")}
        </KdsText>
        <KdsText>{orderItem.sandboxItem.preMediaBrief || ""}</KdsText>
      </div>

      {isOMS && (
        <div className="flex flex-row items-center justify-end gap-4">
          {orderItem.preMediaStatus ===
            OrderItemReadDtoPreMediaStatusEnum.PreMediaSubmitted && (
            <KdsButton
              onPress={() => setPreMediaRequestDetailsModalOpen(true)}
              size="small"
              color="neutral"
              variant="outlined"
            >
              {t("order.myOrdersItemDetails.preMedia.refuseRequest")}
            </KdsButton>
          )}

          <KdsButton
            onPress={() => {
              if (preMediaPropositionFileToAdd) {
                setAddPreMediaPropositionFileModalOpen(true);
              }
              if (preMediaToAccept) {
                setValidatePreMediaModalOpen(true);
              }
            }}
            size="small"
            disabled={
              !(
                orderItem.preMediaStatus ===
                  OrderItemReadDtoPreMediaStatusEnum.None ||
                orderItem.preMediaStatus ===
                  OrderItemReadDtoPreMediaStatusEnum.PreMediaSubmitted ||
                orderItem.preMediaStatus ===
                  OrderItemReadDtoPreMediaStatusEnum.PropositionRefused ||
                orderItem.preMediaStatus ===
                  OrderItemReadDtoPreMediaStatusEnum.PreMediaAccepted
              )
            }
          >
            <KdsText as="span" color="inherit" size="subtitle-xs">
              {preMediaToAccept &&
                t("order.myOrdersItemDetails.preMedia.validateRequest")}
              {preMediaPropositionFileToAdd &&
                t("order.myOrdersItemDetails.preMedia.addFile")}
            </KdsText>
          </KdsButton>
        </div>
      )}

      <ModalPreMediaRequestDetailsForm
        orderItem={orderItem}
        isOpen={preMediaRequestDetailsModalOpen}
        onCloseModal={() => setPreMediaRequestDetailsModalOpen(false)}
      />
      <ModalPreMediaBriefValidation
        orderItem={orderItem}
        isOpen={validatePreMediaModalOpen}
        onCloseModal={() => setValidatePreMediaModalOpen(false)}
      />
      <ModalPreMediaPropositionFileForm
        orderItem={orderItem}
        isOpen={addPreMediaPropositionFileModalOpen}
        onCloseModal={() => setAddPreMediaPropositionFileModalOpen(false)}
      />
    </div>
  );
};
